import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { patientChartAPI, documentAPI, visitSummaryAPI } from '../../lib/api';
import { 
  User,
  Calendar,
  FileText,
  Pill,
  Flask,
  VideoCamera,
  ClipboardText,
  Heart,
  CaretLeft,
  MagnifyingGlass,
  Download,
  Eye,
  Clock,
  Phone,
  Envelope,
  IdentificationCard,
  FirstAidKit,
  Warning,
  CheckCircle,
  X,
  Stethoscope,
  PaperPlaneTilt,
  EnvelopeSimple,
  FilePdf,
  Export
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const tabs = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'visits', label: 'Visit History', icon: Stethoscope },
  { id: 'summaries', label: 'Visit Summaries', icon: EnvelopeSimple },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'labs', label: 'Lab Results', icon: Flask },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'appointments', label: 'Appointments', icon: Calendar },
  { id: 'telehealth', label: 'Telehealth', icon: VideoCamera },
];

export default function PatientChartPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [chart, setChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [visitSummaries, setVisitSummaries] = useState([]);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [exportingChart, setExportingChart] = useState(false);

  const loadPatientChart = useCallback(async () => {
    try {
      const [chartRes, summariesRes] = await Promise.all([
        patientChartAPI.getChart(patientId),
        visitSummaryAPI.getPatientSummaries(patientId).catch(() => ({ data: { summaries: [] } }))
      ]);
      setChart(chartRes.data);
      setVisitSummaries(summariesRes.data.summaries || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading patient chart:', error);
      toast.error('Failed to load patient chart');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadPatientChart();
  }, [loadPatientChart]);

  const sendVisitSummary = async (encounterId = null) => {
    setSendingSummary(true);
    try {
      const response = await visitSummaryAPI.send(patientId, encounterId);
      if (response.data.success) {
        toast.success(`Visit summary sent to ${response.data.patient_email}`, {
          description: `Mode: ${response.data.email_mode}`
        });
        // Refresh summaries
        const summariesRes = await visitSummaryAPI.getPatientSummaries(patientId);
        setVisitSummaries(summariesRes.data.summaries || []);
      } else {
        toast.error('Failed to send visit summary');
      }
    } catch (error) {
      toast.error('Failed to send visit summary');
      process.env.NODE_ENV === "development" && console.error(error);
    } finally {
      setSendingSummary(false);
    }
  };

  const downloadPdf = async (summaryId, patientName, visitDate) => {
    try {
      toast.info('Generating PDF...');
      const response = await visitSummaryAPI.downloadPdf(summaryId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const dateStr = new Date(visitDate).toISOString().split('T')[0];
      link.download = `visit_summary_${patientName.replace(/\s+/g, '_')}_${dateStr}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const exportFullChart = async () => {
    if (!chart || !patient) return;
    
    setExportingChart(true);
    try {
      toast.info('Generating full patient chart PDF...');
      const response = await patientChartAPI.exportChartPdf(patientId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const patientName = `${patient.first_name}_${patient.last_name}`;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `patient_chart_${patientName}_${dateStr}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Patient chart exported as PDF');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Chart export error:', error);
      toast.error('Failed to export patient chart');
    } finally {
      setExportingChart(false);
    }
  };

  const viewDocument = async (doc) => {
    try {
      const response = await documentAPI.getById(doc.id);
      setSelectedDoc(response.data);
      setViewModalOpen(true);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEncounterTypeBadge = (type) => {
    if (type === 'telehealth') {
      return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">Telehealth</span>;
    }
    return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">In-Person</span>;
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      in_progress: 'bg-amber-100 text-amber-700',
      pending: 'bg-slate-100 text-slate-700',
      scheduled: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.pending}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!chart) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <Warning size={48} className="text-amber-500 mx-auto mb-4" />
          <p className="text-slate-600">Patient chart not found</p>
          <button onClick={() => navigate(-1)} className="btn-teal mt-4">Go Back</button>
        </div>
      </div>
    );
  }

  const { patient, summary } = chart;

  return (
    <div className="p-8" data-testid="patient-chart-page">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
        >
          <CaretLeft size={20} />
          Back to Dashboard
        </button>
        
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                {patient.first_name?.[0]}{patient.last_name?.[0]}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {patient.first_name} {patient.last_name}
                </h1>
                <div className="flex items-center gap-4 mt-1 text-slate-500 text-sm">
                  <span className="flex items-center gap-1">
                    <Envelope size={14} />
                    {patient.email}
                  </span>
                  {patient.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={14} />
                      {patient.phone}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                  <IdentificationCard size={12} />
                  Patient ID: {patient.id.slice(0, 8)}...
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-teal-600">{summary.total_visits}</p>
                <p className="text-xs text-slate-500">Total Visits</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">{summary.telehealth_visits}</p>
                <p className="text-xs text-slate-500">Telehealth</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{summary.in_person_visits}</p>
                <p className="text-xs text-slate-500">In-Person</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-600">{summary.total_documents}</p>
                <p className="text-xs text-slate-500">Documents</p>
              </div>
            </div>
          </div>
          
          {/* Export Full Chart Button */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <button
              onClick={exportFullChart}
              disabled={exportingChart}
              className="btn-outline flex items-center gap-2"
              data-testid="export-full-chart-btn"
            >
              {exportingChart ? (
                <>
                  <div className="spinner-sm" />
                  Generating PDF...
                </>
              ) : (
                <>
                  <Export size={18} />
                  Export Full Chart (PDF)
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="border-b border-slate-200">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="overview-tab">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Last Visit */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Last Visit</h3>
                  <p className="text-lg font-semibold text-slate-900">
                    {summary.last_visit ? formatDate(summary.last_visit) : 'No visits yet'}
                  </p>
                </div>
                
                {/* Next Appointment */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Next Appointment</h3>
                  <p className="text-lg font-semibold text-slate-900">
                    {summary.next_appointment ? formatDateTime(summary.next_appointment) : 'None scheduled'}
                  </p>
                </div>
                
                {/* Active Medications */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Active Medications</h3>
                  <p className="text-lg font-semibold text-slate-900">
                    {summary.active_medications}
                  </p>
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {chart.encounters.slice(0, 5).map(enc => (
                    <div key={enc.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          enc.encounter_type === 'telehealth' ? 'bg-purple-100' : 'bg-blue-100'
                        }`}>
                          {enc.encounter_type === 'telehealth' ? (
                            <VideoCamera size={20} className="text-purple-600" />
                          ) : (
                            <Stethoscope size={20} className="text-blue-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {enc.encounter_type === 'telehealth' ? 'Telehealth Visit' : 'Office Visit'}
                          </p>
                          <p className="text-sm text-slate-500">{formatDateTime(enc.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {enc.provider_name && (
                          <span className="text-sm text-slate-500">{enc.provider_name}</span>
                        )}
                        {getStatusBadge(enc.status)}
                      </div>
                    </div>
                  ))}
                  {chart.encounters.length === 0 && (
                    <p className="text-slate-500 text-center py-8">No visits recorded</p>
                  )}
                </div>
              </div>

              {/* Consents Status */}
              {chart.hipaa_consents.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">HIPAA Consents</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {chart.hipaa_consents.map(consent => (
                      <div key={consent.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <CheckCircle size={20} className="text-green-600" weight="fill" />
                        <div>
                          <p className="text-sm font-medium text-green-800">{consent.consent_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                          <p className="text-xs text-green-600">Signed {formatDate(consent.signed_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Visit History Tab */}
          {activeTab === 'visits' && (
            <div className="space-y-4" data-testid="visits-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  All Visits ({chart.encounters.length})
                </h3>
              </div>
              
              {chart.encounters.map(enc => (
                <div key={enc.id} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        enc.encounter_type === 'telehealth' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {enc.encounter_type === 'telehealth' ? (
                          <VideoCamera size={24} className="text-purple-600" />
                        ) : (
                          <Stethoscope size={24} className="text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900">
                            {enc.encounter_type === 'telehealth' ? 'Telehealth Visit' : 'Office Visit'}
                          </h4>
                          {getEncounterTypeBadge(enc.encounter_type)}
                        </div>
                        <p className="text-sm text-slate-500">{formatDateTime(enc.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(enc.status)}
                      {enc.provider_name && (
                        <p className="text-sm text-slate-500 mt-1">{enc.provider_name}</p>
                      )}
                    </div>
                  </div>
                  
                  {(enc.diagnosis || enc.treatment_plan || enc.ai_scribe_notes) && (
                    <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-200">
                      {enc.diagnosis && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Diagnosis</p>
                          <p className="text-sm text-slate-700">{enc.diagnosis}</p>
                        </div>
                      )}
                      {enc.treatment_plan && (
                        <div>
                          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Treatment Plan</p>
                          <p className="text-sm text-slate-700">{enc.treatment_plan}</p>
                        </div>
                      )}
                      {enc.ai_scribe_notes && (
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-slate-500 uppercase mb-1">Clinical Notes</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{enc.ai_scribe_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Addendums Display */}
                  {enc.addendums?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-xs font-medium text-amber-600 uppercase mb-2 flex items-center gap-1">
                        <ClipboardText size={14} />
                        Addendums ({enc.addendums.length})
                      </p>
                      <div className="space-y-2">
                        {enc.addendums.map((add, idx) => (
                          <div key={add.id || idx} className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                            {add.reason && (
                              <p className="text-xs font-medium text-amber-700 mb-1">{add.reason}</p>
                            )}
                            <p className="text-sm text-slate-700">{add.text}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              — {add.added_by_name} • {formatDateTime(add.added_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {chart.encounters.length === 0 && (
                <div className="text-center py-12">
                  <Stethoscope size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No visits recorded</p>
                </div>
              )}
            </div>
          )}

          {/* Visit Summaries Tab */}
          {activeTab === 'summaries' && (
            <div className="space-y-4" data-testid="summaries-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Visit Summaries ({visitSummaries.length})
                </h3>
                <button
                  onClick={() => sendVisitSummary()}
                  disabled={sendingSummary}
                  className="btn-teal flex items-center gap-2"
                  data-testid="send-summary-btn"
                >
                  {sendingSummary ? (
                    <>
                      <div className="spinner-sm" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={18} />
                      Send New Summary
                    </>
                  )}
                </button>
              </div>
              
              {visitSummaries.map(summary => (
                <div key={summary.id} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        summary.visit_type === 'telehealth' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {summary.visit_type === 'telehealth' ? (
                          <VideoCamera size={24} className="text-purple-600" />
                        ) : (
                          <Stethoscope size={24} className="text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-900">
                            {summary.visit_type === 'telehealth' ? 'Telehealth Visit' : 'Office Visit'} Summary
                          </h4>
                          {summary.email_sent && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                              <CheckCircle size={12} weight="fill" />
                              Emailed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{formatDateTime(summary.visit_date)}</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      {summary.provider_name && (
                        <p className="text-sm font-medium text-slate-700">{summary.provider_name}</p>
                      )}
                      <button
                        onClick={() => downloadPdf(summary.id, summary.patient_name, summary.visit_date)}
                        className="p-2 bg-teal-100 hover:bg-teal-200 rounded-lg transition-colors"
                        title="Download PDF"
                        data-testid={`download-pdf-${summary.id}`}
                      >
                        <FilePdf size={18} className="text-teal-600" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {summary.reason_for_visit && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Reason for Visit</p>
                        <p className="text-sm text-slate-700">{summary.reason_for_visit}</p>
                      </div>
                    )}
                    {summary.vitals && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Vitals</p>
                        <div className="text-sm text-slate-700 space-y-0.5">
                          {summary.vitals.blood_pressure && <p>BP: {summary.vitals.blood_pressure}</p>}
                          {summary.vitals.temperature && <p>Temp: {summary.vitals.temperature}°F</p>}
                          {summary.vitals.pulse && <p>Pulse: {summary.vitals.pulse} bpm</p>}
                        </div>
                      </div>
                    )}
                    {summary.assessment && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Assessment</p>
                        <p className="text-sm text-slate-700">{summary.assessment}</p>
                      </div>
                    )}
                    {summary.prescriptions && summary.prescriptions.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Prescriptions</p>
                        <ul className="text-sm text-slate-700">
                          {summary.prescriptions.map((rx, idx) => {
                            // Handle nested medication object
                            const medName = rx.medication?.name 
                              ? `${rx.medication.name}${rx.medication.strength ? ` ${rx.medication.strength}` : ''}`
                              : (rx.name || rx.medication_name || 'Unknown');
                            return (
                              <li key={rx.id || idx}>{medName} - {rx.dosage}</li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    {summary.lab_orders && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Lab Orders</p>
                        <p className="text-sm text-slate-700">{summary.lab_orders}</p>
                      </div>
                    )}
                    {summary.imaging_orders && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Imaging Orders</p>
                        <p className="text-sm text-slate-700">{summary.imaging_orders}</p>
                      </div>
                    )}
                    {summary.followup_scheduled && summary.followup_date && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase mb-1">Follow-up</p>
                        <p className="text-sm text-slate-700">{formatDate(summary.followup_date)}</p>
                      </div>
                    )}
                  </div>
                  
                  {summary.email_sent && summary.email_sent_at && (
                    <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-500">
                      <EnvelopeSimple size={14} />
                      Sent to {summary.patient_email} on {formatDateTime(summary.email_sent_at)}
                    </div>
                  )}
                </div>
              ))}
              
              {visitSummaries.length === 0 && (
                <div className="text-center py-12">
                  <EnvelopeSimple size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No visit summaries on file</p>
                  <p className="text-sm text-slate-400 mt-2">
                    Summaries are automatically sent after telehealth visits or can be sent manually.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-4" data-testid="documents-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  All Documents ({chart.documents.length})
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {chart.documents.map(doc => (
                  <div key={doc.id} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                          <FileText size={20} className="text-teal-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">
                            {doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-xs text-slate-500">{doc.file_name}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => viewDocument(doc)}
                        className="p-2 hover:bg-slate-200 rounded-lg"
                        title="View document"
                      >
                        <Eye size={18} className="text-slate-500" />
                      </button>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                      <span>{formatDate(doc.uploaded_at)}</span>
                      {doc.expiration_date && (
                        <span className={doc.is_expired ? 'text-red-500' : doc.is_expiring_soon ? 'text-amber-500' : ''}>
                          Exp: {formatDate(doc.expiration_date)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {chart.documents.length === 0 && (
                <div className="text-center py-12">
                  <FileText size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No documents uploaded</p>
                </div>
              )}
            </div>
          )}

          {/* Lab Results Tab */}
          {activeTab === 'labs' && (
            <div className="space-y-4" data-testid="labs-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Lab Results ({chart.lab_results.length})
                </h3>
              </div>
              
              {chart.lab_results.map(lab => (
                <div key={lab.id} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Flask size={20} className="text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{lab.test_name}</p>
                        <p className="text-sm text-slate-500">{lab.lab_name} • {formatDate(lab.test_date)}</p>
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${
                      lab.is_abnormal ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {lab.value} {lab.unit}
                    </div>
                  </div>
                  {lab.reference_range && (
                    <p className="text-xs text-slate-500">Reference: {lab.reference_range}</p>
                  )}
                </div>
              ))}
              
              {chart.lab_results.length === 0 && (
                <div className="text-center py-12">
                  <Flask size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No lab results on file</p>
                </div>
              )}
            </div>
          )}

          {/* Medications Tab */}
          {activeTab === 'medications' && (
            <div className="space-y-4" data-testid="medications-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Medication History ({chart.medication_reconciliations.length} reconciliations)
                </h3>
              </div>
              
              {chart.medication_reconciliations.map(rec => (
                <div key={rec.id} className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <p className="font-medium text-slate-900">Reconciliation: {formatDate(rec.created_at)}</p>
                    {getStatusBadge(rec.status)}
                  </div>
                  <div className="space-y-2">
                    {rec.medications?.map((med, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <div className="flex items-center gap-3">
                          <Pill size={18} className={med.status === 'active' ? 'text-green-500' : 'text-slate-400'} />
                          <div>
                            <p className="font-medium text-slate-900">{med.name}</p>
                            <p className="text-sm text-slate-500">{med.dosage} - {med.frequency}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          med.status === 'active' ? 'bg-green-100 text-green-700' :
                          med.status === 'discontinued' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {med.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {chart.medication_reconciliations.length === 0 && (
                <div className="text-center py-12">
                  <Pill size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No medication records on file</p>
                </div>
              )}
            </div>
          )}

          {/* Appointments Tab */}
          {activeTab === 'appointments' && (
            <div className="space-y-4" data-testid="appointments-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Appointments ({chart.appointments.length})
                </h3>
              </div>
              
              {chart.appointments.map(apt => (
                <div key={apt.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {apt.appointment_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </p>
                      <p className="text-sm text-slate-500">{formatDateTime(apt.scheduled_time)}</p>
                    </div>
                  </div>
                  {getStatusBadge(apt.status)}
                </div>
              ))}
              
              {chart.appointments.length === 0 && (
                <div className="text-center py-12">
                  <Calendar size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No appointments on file</p>
                </div>
              )}
            </div>
          )}

          {/* Telehealth Tab */}
          {activeTab === 'telehealth' && (
            <div className="space-y-4" data-testid="telehealth-tab">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Telehealth Sessions ({chart.video_calls.length})
                </h3>
              </div>
              
              {chart.video_calls.map(call => (
                <div key={call.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <VideoCamera size={20} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{call.room_name}</p>
                      <p className="text-sm text-slate-500">{formatDateTime(call.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">
                      {call.type === 'telehealth' ? 'Patient Call' : 'Staff Meeting'}
                    </span>
                    {getStatusBadge(call.status)}
                  </div>
                </div>
              ))}
              
              {chart.video_calls.length === 0 && (
                <div className="text-center py-12">
                  <VideoCamera size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No telehealth sessions on file</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Document View Modal */}
      {viewModalOpen && selectedDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">{selectedDoc.file_name}</h3>
                <p className="text-sm text-slate-500">
                  {selectedDoc.document_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedDoc(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] flex items-center justify-center bg-slate-100">
              {selectedDoc.mime_type?.startsWith('image/') ? (
                <img
                  src={`data:${selectedDoc.mime_type};base64,${selectedDoc.file_data}`}
                  alt={selectedDoc.file_name}
                  className="max-w-full max-h-full object-contain"
                />
              ) : selectedDoc.mime_type === 'application/pdf' ? (
                <iframe
                  src={`data:${selectedDoc.mime_type};base64,${selectedDoc.file_data}`}
                  className="w-full h-[70vh]"
                  title={selectedDoc.file_name}
                />
              ) : (
                <p className="text-slate-500">Preview not available for this file type</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
